/**
 * Attribution data capture and persistence
 * Captures UTM parameters, gclid, and platform from URL and cookies
 */

const STORAGE_KEY = 'azai_attribution';
const GCLID_COOKIE_NAME = '_gcl_aw';

/**
 * List of attribution parameters to capture
 */
const ATTRIBUTION_PARAMS = [
  'gclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'platform',
];

/**
 * Parse URL parameters from current location
 * @returns {Object} - Object with URL parameter values
 */
const getUrlParams = () => {
  try {
    if (typeof window === 'undefined') {
      return {};
    }
    
    const params = new URLSearchParams(window.location.search);
    const result = {};
    
    ATTRIBUTION_PARAMS.forEach((param) => {
      const value = params.get(param);
      if (value) {
        result[param] = value;
      }
    });
    
    return result;
  } catch (error) {
    console.error('[Attribution] Error parsing URL params:', error);
    return {};
  }
};

/**
 * Extract gclid from _gcl_aw cookie
 * Cookie format: GCL.timestamp.gclid
 * @returns {string|null} - The gclid value or null
 */
const getGclidFromCookie = () => {
  try {
    if (typeof document === 'undefined') {
      return null;
    }
    
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === GCLID_COOKIE_NAME && value) {
        // Parse GCL.timestamp.gclid format
        const parts = value.split('.');
        if (parts.length >= 3) {
          return parts.slice(2).join('.'); // Handle gclids with dots
        }
      }
    }
    return null;
  } catch (error) {
    console.error('[Attribution] Error reading gclid cookie:', error);
    return null;
  }
};

/**
 * Get stored attribution data from sessionStorage
 * @returns {Object} - Stored attribution data or empty object
 */
const getStoredAttribution = () => {
  try {
    if (typeof sessionStorage === 'undefined') {
      return {};
    }
    
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('[Attribution] Error reading stored attribution:', error);
    return {};
  }
};

/**
 * Store attribution data in sessionStorage
 * @param {Object} data - Attribution data to store
 */
const storeAttribution = (data) => {
  try {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('[Attribution] Error storing attribution:', error);
  }
};

/**
 * Initialize attribution capture
 * Captures URL params and cookie gclid, merges with existing data
 * @returns {Object} - Complete attribution data
 */
export const initAttribution = () => {
  try {
    // Get existing stored data
    const existingData = getStoredAttribution();
    
    // Get new URL params
    const urlParams = getUrlParams();
    
    // Check for gclid in cookie if not in URL
    if (!urlParams.gclid) {
      const cookieGclid = getGclidFromCookie();
      if (cookieGclid) {
        urlParams.gclid = cookieGclid;
      }
    }
    
    // Merge new params with existing (new values override)
    const mergedData = {
      ...existingData,
      ...urlParams,
      captured_at: existingData.captured_at || Date.now(),
      last_updated: Date.now(),
    };
    
    // Store merged data
    storeAttribution(mergedData);
    
    return mergedData;
  } catch (error) {
    console.error('[Attribution] Error initializing attribution:', error);
    return {};
  }
};

/**
 * Get current attribution data
 * @returns {Object} - Attribution data from storage
 */
export const getAttribution = () => {
  try {
    return getStoredAttribution();
  } catch (error) {
    console.error('[Attribution] Error getting attribution:', error);
    return {};
  }
};

/**
 * Get specific attribution value
 * @param {string} key - The attribution key to get
 * @returns {string|null} - The value or null
 */
export const getAttributionValue = (key) => {
  try {
    const data = getStoredAttribution();
    return data[key] || null;
  } catch (error) {
    console.error('[Attribution] Error getting attribution value:', error);
    return null;
  }
};

/**
 * Extract domain from URL for privacy-safe logging
 * @param {string} url - Full URL
 * @returns {string} - Just the domain
 */
export const extractDomain = (url) => {
  try {
    if (!url) return '';
    
    // Handle URLs without protocol
    let urlToParse = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      urlToParse = 'https://' + url;
    }
    
    const parsed = new URL(urlToParse);
    return parsed.hostname.replace(/^www\./, '');
  } catch (error) {
    // If URL parsing fails, try to extract domain manually
    try {
      const cleaned = url.replace(/^(https?:\/\/)?(www\.)?/, '');
      return cleaned.split('/')[0].split('?')[0];
    } catch {
      return url;
    }
  }
};

/**
 * Get platform value from attribution or default
 * @returns {string} - Platform value or 'direct'
 */
export const getPlatform = () => {
  try {
    const attribution = getAttribution();
    return attribution.platform || 'direct';
  } catch (error) {
    return 'direct';
  }
};

export default {
  initAttribution,
  getAttribution,
  getAttributionValue,
  extractDomain,
  getPlatform,
};
